import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { lumi } from "@/lib/lumi"
import {ArrowLeft, ArrowRight, Search, Filter, Loader2, TicketCheck} from 'lucide-react'

const statusLabels: Record<string, string> = {
  nouveau: "Nouveau", en_attente: "En attente", en_cours: "En cours",
  attente_client: "Att. client", resolu: "Resolu", ferme: "Ferme",
  escalade: "Escalade", planifie: "Planifie", annule: "Annule",
}

const statusColor: Record<string, string> = {
  nouveau: "text-cyan-400 bg-cyan-500/10", en_attente: "text-amber-400 bg-amber-500/10",
  en_cours: "text-blue-400 bg-blue-500/10", attente_client: "text-orange-400 bg-orange-500/10",
  resolu: "text-emerald-400 bg-emerald-500/10", ferme: "text-slate-400 bg-slate-500/10",
  escalade: "text-red-400 bg-red-500/10", planifie: "text-purple-400 bg-purple-500/10",
  annule: "text-slate-500 bg-slate-600/10",
}

const categoryLabels: Record<string, string> = {
  poste_de_travail: "Poste de travail", email: "Email", imprimante: "Imprimante",
  reseau_internet: "Reseau / Internet", acces_mot_de_passe: "Acces / MDP",
  serveur_infrastructure: "Serveur", sauvegarde: "Sauvegarde",
  configuration: "Configuration", autre: "Autre",
}

export default function AdminTickets() {
  const [tickets, setTickets] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [filterStatus, setFilterStatus] = useState("")
  const [filterPriority, setFilterPriority] = useState("")
  const [filterCategory, setFilterCategory] = useState("")

  useEffect(() => {
    loadTickets()
  }, [])

  const loadTickets = async () => {
    try {
      const { list } = await lumi.entities.tickets.list({
        sort: { createdAt: -1 },
        limit: 500,
      })
      setTickets(list)
    } catch (err) {
      console.error("Erreur:", err)
    } finally {
      setLoading(false)
    }
  }

  const filtered = tickets.filter((t) => {
    if (filterStatus && t.status !== filterStatus) return false
    if (filterPriority && t.priority !== filterPriority) return false
    if (filterCategory && t.category !== filterCategory) return false
    if (search) {
      const q = search.toLowerCase()
      return (
        t.subject?.toLowerCase().includes(q) ||
        t.clientName?.toLowerCase().includes(q) ||
        t.clientEmail?.toLowerCase().includes(q) ||
        t.company?.toLowerCase().includes(q)
      )
    }
    return true
  })

  return (
    <div className="min-h-screen pt-24 lg:pt-32 pb-20">
      <div className="section-padding">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" />
          Retour au dashboard
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <TicketCheck className="w-6 h-6 text-cyan-400" />
            Gestion des tickets
            <span className="text-sm font-normal text-slate-400">({filtered.length})</span>
          </h1>
        </div>

        {/* Filters */}
        <div className="card !p-4 mb-6 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, email, sujet..."
              className="input-field !pl-10 !py-2.5 text-sm"
            />
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="input-field !w-auto !py-2.5 text-sm">
            <option value="">Tous les statuts</option>
            {Object.entries(statusLabels).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="input-field !w-auto !py-2.5 text-sm">
            <option value="">Toutes priorites</option>
            <option value="faible">Faible</option>
            <option value="normale">Normale</option>
            <option value="haute">Haute</option>
            <option value="urgente">Urgente</option>
          </select>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="input-field !w-auto !py-2.5 text-sm">
            <option value="">Toutes categories</option>
            {Object.entries(categoryLabels).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        {/* Tickets list */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="card !p-12 text-center">
            <Filter className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Aucun ticket trouve</h3>
            <p className="text-sm text-slate-400">Modifiez vos filtres pour voir plus de resultats.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((ticket) => (
              <Link
                key={ticket._id}
                to={`/admin/tickets/${ticket._id}`}
                className="card !p-4 flex flex-col sm:flex-row sm:items-center gap-3 group hover:border-cyan-500/40"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor[ticket.status] || ""}`}>
                      {statusLabels[ticket.status] || ticket.status}
                    </span>
                    <span className={`text-xs font-medium ${
                      ticket.priority === "urgente" ? "text-red-400" :
                      ticket.priority === "haute" ? "text-amber-400" :
                      "text-slate-500"
                    }`}>
                      {ticket.priority}
                    </span>
                    <span className="text-xs text-slate-600">
                      {categoryLabels[ticket.category] || ticket.category}
                    </span>
                  </div>
                  <h3 className="text-sm font-medium text-white truncate group-hover:text-cyan-400 transition-colors">
                    {ticket.subject}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {ticket.clientName} ({ticket.clientEmail})
                    {ticket.company ? ` - ${ticket.company}` : ""}
                    {" "}&middot; {new Date(ticket.createdAt).toLocaleDateString("fr-FR")}
                    {ticket.assignedTo ? ` &middot; Assigne: ${ticket.assignedTo}` : ""}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
